import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  MoreHorizontal,
  Check,
  Undo2,
  Archive,
  BellOff,
  Clock,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { formatListTimestamp, timestampBand } from '@/lib/format'
import {
  getNotificationIcon,
  getNotificationLink,
  formatNotificationTitle,
  formatNotificationBody,
} from '@/lib/notification-links'
import type { Database } from '@/lib/types/database'

type Notification = Database['public']['Tables']['notifications']['Row']

interface NotificationRowProps {
  notification: Notification
  locale?: string
  selected?: boolean
  onSelect?: (id: string, selected: boolean) => void
  onMarkRead: (id: string) => void
  onMarkUnread?: (id: string) => void
  onArchive: (id: string) => void
  onDelete?: (id: string) => void
  onSnooze?: (hours: number) => void
  onMuteEvent?: (eventId: string) => void
  onClose?: () => void
  compact?: boolean
  /**
   * True when a group header above the row already names the day (the
   * notifications page bands its list by «I dag» / «I går»). The bell popover
   * is a flat list of ten and has no such header, so it must not be told this —
   * without the day-word, yesterday's 11:57 and today's are the same string.
   * Defaults to false so a new consumer gets the self-sufficient stamp.
   */
  dayInHeader?: boolean
}

function NotificationRowImpl({
  notification: n,
  locale = 'nb',
  selected,
  onSelect,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onDelete,
  onSnooze,
  onMuteEvent,
  onClose,
  compact,
  dayInHeader,
}: NotificationRowProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const Icon = getNotificationIcon(n.type)
  const eventId = (n.metadata as { event_id?: string } | null)?.event_id

  /* The stamp is the row's only answer to «when». It used to be
     `toLocaleTimeString` for every row regardless of age, so everything under
     «Eldre» — a band that runs from eight days back to whenever — showed a bare
     clock time. `formatListTimestamp` adds the weekday inside the week and the
     date beyond it; the day-word for yesterday is added here, where the
     translation lives, and only when no header carries it. */
  const stamp = formatListTimestamp(n.created_at, locale)
  const timestamp =
    !dayInHeader && timestampBand(n.created_at) === 'yesterday'
      ? `${t('notifications.yesterday', { defaultValue: 'I går' })} ${stamp}`
      : stamp

  const click = () => {
    onMarkRead(n.id)
    navigate(getNotificationLink(n))
    onClose?.()
  }

  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 px-3 sm:px-4 transition-colors',
        compact ? 'py-2.5' : 'py-3',
        'hover:bg-[var(--surface-muted)]/60',
        !n.read && 'bg-[var(--color-primary-500)]/5'
      )}
    >
      {/* D-17 · selection appears on demand, not on every row forever.
          A checkbox on all eight rows made «velg» the first thing the page
          offered, ahead of the notifications themselves — and nobody arrives
          at this page to do bulk selection. It reveals on hover, on keyboard
          focus, and whenever anything is actually selected, so the mode is
          discoverable both ways round.
          `opacity`, not conditional rendering: the box keeps its 16px of
          layout, so a row does not jump sideways under the cursor, and it
          stays in the accessibility tree for screen readers throughout. */}
      {onSelect && (
        /* 16px of ink, 44px of target — via a LABEL, not padding on the input.
           Padding was the first attempt and it does nothing: a checkbox is a
           replaced element, so it ignores padding and the box stayed 16px
           (measured, which is the only reason I know). A wrapping label with
           `-m-3 p-3` grows the hit area without moving the tick or anything
           beside it, and clicking a label toggles its control for free. */
        <label className="-m-3.5 flex shrink-0 cursor-pointer items-center self-center p-3.5">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => onSelect(n.id, e.target.checked)}
            aria-label={t('notifications.selectRowNamed', { title: formatNotificationTitle(n, t) })}
            className={cn(
              'size-4 accent-[var(--border-selected)] cursor-pointer',
              'transition-opacity duration-[var(--duration-fast)]',
              'group-hover:opacity-100 focus-visible:opacity-100',
              // A phone has no hover: an invisible box that still selects on a tap was a trap
              // (remaining-surfaces review 25.10.2026). Where nothing hovers, it is always shown.
              '[@media(hover:none)]:opacity-100',
              selected ? 'opacity-100' : 'opacity-0'
            )}
            onClick={(e) => e.stopPropagation()}
          />
        </label>
      )}

      {/* Klikkbar zone: avatar + tekst */}
      {/* min-h-11 · the row IS the page's primary target and measured 41px,
          three short of the floor. The padding around it varies with `compact`,
          so the floor goes on the target rather than on the padding. */}
      <button
        type="button"
        onClick={click}
        className="flex min-h-11 flex-1 items-center gap-3 text-left min-w-0 cursor-pointer"
      >
        {/* B-1 · the medallion had an invalid inline background and therefore
            never had one; D-5 · the glyph is wayfinding, so it takes the
            secondary neutral and nothing else. */}
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--text-secondary)]">
          <Icon className="size-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5 min-w-0">
            {!n.read && (
              <span
                className="inline-block size-2 rounded-full bg-[var(--color-primary-500)] shrink-0 mt-1.5"
                aria-label={t('notifications.unread', { defaultValue: 'Ulest' })}
              />
            )}
            {/* line-clamp-2, not truncate: the title IS the notification, and a
                single clipped line ("Matbestilling (svar) …") is unreadable in
                the narrow mobile row. Still bounded so rows stay scannable. */}
            {/* D-18 · read drops to the secondary neutral, not just to a
                lighter weight. Both states used to sit at --text-primary and
                differ by weight alone, which is a distinction you have to
                compare two rows to notice. Hierarchy by contrast: unread is
                full contrast + semibold + the dot, read is secondary. */}
            <p
              className={cn(
                'text-sm leading-snug line-clamp-2 min-w-0',
                n.read
                  ? 'text-[var(--text-secondary)] font-normal'
                  : 'text-[var(--text-primary)] font-semibold'
              )}
            >
              {formatNotificationTitle(n, t)}
            </p>
          </div>
          {/* 13px, not `text-2xs` (11px): the body is the notification's own
              sentence, the thing a reader actually reads, and text-2xs is under
              the 12.5px mobile floor (22d). Same for the timestamp below.
              (Utility named explicitly in 0.10 — bare «11px» read as if it meant
              text-caption, which is 12.5px since 33a.) */}
          {formatNotificationBody(n, t) && (
            <p className="text-small text-[var(--text-secondary)] mt-0.5 line-clamp-2">
              {formatNotificationBody(n, t)}
            </p>
          )}
        </div>
      </button>

      {/* Høyre side: tid + meny — stacked på mobil, side-by-side på desktop */}
      <div className="flex flex-col items-end gap-0.5 shrink-0 self-center">
        <time
          dateTime={n.created_at}
          className="text-small text-[var(--text-muted)] whitespace-nowrap tabular-nums"
        >
          {timestamp}
        </time>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              /* 44px on a phone, 32px from lg: `icon-sm`, which the page used to spell by hand. */
              variant="quiet"
              size="icon-sm"
              aria-label={t('notifications.actionsNamed', { title: formatNotificationTitle(n, t) })}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Read ⇄ unread, both directions. `onMarkUnread` was declared in this
              component's props, `markAsUnread` was built in the hook and
              `notifications.markUnread` was translated into nb AND en — and the
              menu rendered neither the item nor the prop, so the round trip was
              three-quarters built and zero-quarters reachable. Measured on the
              live page: a read row's menu offered snooze, mute and archive only.
              Auto-mark-as-read fires 800 ms after the list settles, so «put that
              one back» is the exit this page most needs. */}
          {!n.read ? (
            <DropdownMenuItem onClick={() => onMarkRead(n.id)}>
              <Check className="size-4" aria-hidden="true" />
              {t('notifications.markRead', { defaultValue: 'Marker som lest' })}
            </DropdownMenuItem>
          ) : (
            onMarkUnread && (
              <DropdownMenuItem onClick={() => onMarkUnread(n.id)}>
                <Undo2 className="size-4" aria-hidden="true" />
                {t('notifications.markUnread', { defaultValue: 'Marker som ulest' })}
              </DropdownMenuItem>
            )
          )}
          {onSnooze && (
            <>
              <DropdownMenuItem onClick={() => onSnooze(1)}>
                <Clock className="size-4" aria-hidden="true" />
                {t('notifications.snooze1h', { defaultValue: 'Snooze 1 time' })}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onSnooze(8)}>
                <Clock className="size-4" aria-hidden="true" />
                {t('notifications.snooze8h', { defaultValue: 'Snooze 8 timer' })}
              </DropdownMenuItem>
            </>
          )}
          {onMuteEvent && eventId && (
            <DropdownMenuItem onClick={() => onMuteEvent(eventId)}>
              <BellOff className="size-4" aria-hidden="true" />
              {t('notifications.muteEvent', { defaultValue: 'Slå av varsler for dette arrangementet' })}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onArchive(n.id)}>
            <Archive className="size-4" aria-hidden="true" />
            {t('notifications.archive', { defaultValue: 'Arkiver' })}
          </DropdownMenuItem>
          {onDelete && (
            <DropdownMenuItem destructive onClick={() => onDelete(n.id)}>
              <Trash2 className="size-4" aria-hidden="true" />
              {t('notifications.delete', { defaultValue: 'Slett' })}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export const NotificationRow = memo(NotificationRowImpl)
